import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Flower2, Loader2 } from "lucide-react";
import ProductActions from "../components/common/ProductActions";
import { catalogService, resolveImageUrl } from "../services/catalogService";
import { iconFor } from "../lib/iconLibrary";
import { useLanguage } from "../context/LanguageContext";

// Task 02 (C01) — four image views per variety. The gallery always renders
// 4 independent slots; until real photos arrive all slots hold the same
// placeholder image (managed per-slot from the admin panel).
export default function VarietyPage() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const [variety, setVariety] = useState(null);
  const [related, setRelated] = useState([]);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setVariety(null);
    setRelated([]);
    setMissing(false);
    setActive(0);
    catalogService
      .getVariety(slug)
      .then(setVariety)
      .catch(() => setMissing(true));
    catalogService
      .getRelated(slug)
      .then(setRelated)
      .catch(() => setRelated([]));
  }, [slug]);

  const images = variety?.images?.length ? variety.images : variety?.image ? [variety.image] : [];

  return (
    <main className="min-h-screen bg-[#F6F2EC] text-[#252525] pt-28 pb-24" data-testid="variety-page">
      <div className="container-tessa">
        <Link to="/" className="flex w-fit items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#817B74] hover:text-[#D92A47] transition-colors mb-10">
          <ArrowLeft size={14} /> {t("pages.backHome")}
        </Link>

        {missing ? (
          <div className="border border-black/10 bg-white p-12 text-center" data-testid="variety-not-found">
            <p className="font-serif text-2xl">{t("pages.variety.notFound")}</p>
            <Link to="/" className="inline-block mt-4 text-[12px] uppercase tracking-[0.14em] text-[#D92A47] border-b border-[#D92A47]">
              {t("pages.variety.browse")}
            </Link>
          </div>
        ) : !variety ? (
          <div className="text-[#817B74] py-20 text-center" data-testid="variety-loading">
            <Loader2 className="animate-spin inline mr-2" size={16} />{t("pages.loading")}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div data-testid="variety-gallery">
              <div className="relative aspect-[4/5] overflow-hidden bg-white border border-black/10">
                {images[active] ? (
                  <img
                    key={active}
                    src={resolveImageUrl(images[active])}
                    alt={`${variety.name} — view ${active + 1}`}
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="variety-main-image"
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-[#817B74]" data-testid="variety-main-image">
                    <span className="flex flex-col items-center gap-2 text-[11px] uppercase tracking-[0.16em]">
                      <Flower2 size={28} strokeWidth={1.2} /> {t("pages.variety.photoSoon")}
                    </span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3" role="tablist" aria-label={t("pages.variety.imageViews")}>
                {images.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === active}
                    onClick={() => setActive(i)}
                    data-testid={`variety-thumb-${i + 1}`}
                    className={`relative aspect-square overflow-hidden border transition-colors ${
                      i === active ? "border-[#D92A47]" : "border-black/10 hover:border-[#D92A47]/60"
                    }`}
                  >
                    <span className={`absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${
                      i === active ? "bg-[#D92A47] text-white" : "bg-white/85 text-[#252525]"
                    }`}>
                      {i + 1}
                    </span>
                    {src ? (
                      <img
                        src={resolveImageUrl(src)}
                        alt={`${variety.name} thumbnail ${i + 1}`}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:pt-4">
              <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]" data-testid="variety-category">
                {variety.category_label}
              </span>
              <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] mt-3" data-testid="variety-name">
                {variety.name}
              </h1>
              {variety.description_en && (
                <p className="text-[#817B74] leading-relaxed mt-6 max-w-md" data-testid="variety-description">
                  {variety.description_en}
                </p>
              )}
              {variety.qualities?.length > 0 && (
                <div className="mt-8" data-testid="variety-qualities">
                  <span className="uppercase tracking-[0.14em] text-[11px] text-[#817B74]">{t("season.idealPara")}</span>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {variety.qualities.map((q, i) => {
                      const QIcon = iconFor(q.icon);
                      return (
                        <span
                          key={`${q.icon}-${i}`}
                          className="inline-flex items-center gap-2 border border-[#D92A47]/30 bg-white px-4 py-2 text-sm text-[#252525]"
                          data-testid={`variety-quality-${i}`}
                        >
                          <QIcon size={16} className="text-[#D92A47]" />
                          {q.label_en}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {variety.colors?.length > 0 && (
                <div className="mt-8">
                  <span className="uppercase tracking-[0.14em] text-[11px] text-[#817B74]">{t("pages.variety.tones")}</span>
                  <div className="flex gap-2 mt-2">
                    {variety.colors.map((c) => (
                      <span key={c} className="h-6 w-6 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-10">
                <ProductActions
                  variety={{
                    id: variety.id,
                    name: variety.name,
                    image: images[0],
                    category_label: variety.category_label,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {variety && related.length > 0 && (
          <section className="mt-20 border-t border-black/10 pt-12" data-testid="related-section">
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">{t("pages.variety.relatedEyebrow")}</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-8">{t("pages.variety.relatedTitle")}</h2>
            <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((r) => (
                <li key={r.id} className="bg-white border border-black/10" data-testid={`related-card-${r.id}`}>
                  <Link to={`/variety/${r.id}`} className="block aspect-square overflow-hidden bg-[#F6F2EC]">
                    <img src={resolveImageUrl(r.image)} alt={r.name} className="h-full w-full object-cover hover:scale-[1.03] transition-transform duration-500" />
                  </Link>
                  <div className="p-4">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[#817B74]">{r.category_label}</span>
                    <h3 className="font-serif text-lg text-[#252525] mt-0.5">
                      <Link to={`/variety/${r.id}`} className="hover:text-[#D92A47] transition-colors">{r.name}</Link>
                    </h3>
                    <div className="flex gap-1.5 mt-2">
                      {(r.colors || []).slice(0, 4).map((c) => (
                        <span key={c} className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
